import store from "@/store";
import { marked } from "marked";
import { JSONParse } from "_lib/utils/utils";
import { formatWebplImg } from "_lib/utils/utilsImg";
import { getIndexFromArrayByKeyValue } from "_lib/utils/utilsArray";
import { decimalToHexColor } from "_lib/utils/utilsColor";
import { getEmojiHtml } from '@/directives/emojiHtmlDirective';
import { calcUserName, calcUserRoleColor } from "_lib/CalcUserInfo";
import { ON_RISK_IMG_URL } from "./constants/message";
import { ERROR_IMG } from '_lib/constants/global'
import { isValidURL } from "./purify";
import emojiRender from '_lib/emoji/EmojiRender'

const pointReg = /&#46;/g;
function protectCustomTags(str) {
  // 匹配 [customxxx_yyy] 形式
  const customReg = /\[custom[^\[\]]+?\]/g;
  const customEmojis = [];
  // 替换为占位符
  const protectedStr = str.replace(customReg, (match) => {
    customEmojis.push(match);
    return `@@CUSTOM${customEmojis.length - 1}@@`;
  });
  return { protectedStr, customEmojis };
}
function restoreCustomTags(html, customEmojis) {
  if (customEmojis.length === 0) return html;
  // 还原占位符
  return html.replace(/@@CUSTOM(\d+)@@/g, (_, idx) => customEmojis[idx]);
}
function addCodeSpacing(html) {
  // 创建临时 DOM 来处理
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  const pElements = tempDiv.querySelectorAll('p');
  pElements.forEach(p => {
    const codes = p.querySelectorAll('code');
    codes.forEach(code => {
      // 检查前面是否有内容
      let prevNode = code.previousSibling;
      while (prevNode && prevNode.nodeType === Node.TEXT_NODE && !prevNode.textContent.trim()) {
        prevNode = prevNode.previousSibling;
      }
      if (prevNode) {
        code.style.marginLeft = '4px';
      }
      
      // 检查后面是否有内容  
      let nextNode = code.nextSibling;
      while (nextNode && nextNode.nodeType === Node.TEXT_NODE && !nextNode.textContent.trim()) {
        nextNode = nextNode.nextSibling;
      }
      if (nextNode) {
        code.style.marginRight = '4px';
      }
    });
  });
  return tempDiv.innerHTML;
}
function replaceBotKeywords(str) {
  // 匹配 /{t:xxx,b:xxx,c:xxx} 格式的正则表达式
  const regex = /\/(\{[^}]*\})/g;
  
  return str.replace(regex, (match, content) => {
    // 提取 t、b、c 的值
    const tMatch = content.match(/t:([^,}]+)/);
    const bMatch = content.match(/b:([^,}]+)/);
    const cMatch = content.match(/c:([^,}]+)/);
    
    // 检查是否包含 t、b、c 并且它们都有值
    if (tMatch && bMatch && cMatch) {
      const tValue = tMatch[1];
      const bValue = bMatch[1];
      const cValue = cMatch[1];
      // 返回替换后的 HTML 标签
      return `<span class="bot-keyword" bot-id="${bValue}" command-id="${cValue}">${tValue}</span>`;
    }
    
    // 如果不匹配格式，返回原字符串
    return match;
  });
}
export default function markdownToHtml(msg, notMarkdown, no_img) {
  let str = typeof msg === "string" ? msg : msg?.msg || msg?.content || msg?.anc;
  const addition = msg?.addition ? JSONParse(msg?.addition) : null;
  if (!str) return "";
  // 将有序列表的 x&#46; 替换为 x.
  str = str.replace(/(\d+)\s*&#46;/g, '$1.');
  str = str.replace(/&lt;u>/g, '<u>')
  str = str.replace(/&lt;\/u>/g, '</u>')
  let { protectedStr, customEmojis } = protectCustomTags(str);
  str = protectedStr;
  let str_reg_arr = regParser(str, msg);
  const renderer = new marked.Renderer();
  renderer.image = function (href, title, text) {
    if (no_img) {
      return '[图片]'
    }
    let addition = JSONParse(msg.addition);
    if (!href) {
      return `<img src="${ERROR_IMG[document.body.getAttribute('theme') || 'dark']}" alt="image">`;
    }
    let imgAttrs = `alt="${text || 'image'}"`;
    if (title) {
      imgAttrs += ` title="${title}"`;
    }
    let imgSrc = ''
    if (addition?.img_files_info) {
      // 防止 . 转义导致匹配错误
      let info = addition.img_files_info.find((info) => info.url?.split('?')[0] === href.replace(pointReg, "."));
      if (info) {
        imgSrc = info.url
        imgAttrs += ` info-width="${info.width}" info-height="${info.height}"`;
      } else {
        imgSrc = href
      }
    } else {
      imgSrc = href;
    }
    imgSrc = formatWebplImg(imgSrc)
    if (checkIsOnRiskPic(imgSrc)) {
      imgAttrs += ' rask-pic="1"';
    }
    return `<img src="${imgSrc}" ${imgAttrs}>`;
  };
  renderer.link = function (href, title, text) {
    const isValid = isValidURL(href);
    if (!isValid) {
      return text;
    }
    // 匹配url+中文
    const regex = /^(https?:\/\/[a-zA-Z0-9-._~:/?#@!$&'()*+,;=%]*)([^a-zA-Z0-9-._~:/?#@!$&'()*+,;=%]+.*)/;
    const matches = href.match(regex);
    if (matches) {
      const [, baseURL, chinesePart] = matches;
      return `<a href="${baseURL}" title="${title || ""}" target="_blank">${baseURL}</a>${chinesePart}`;
    }
    return `<a href="${href}" title="${title || ""}" target="_blank">${text}</a>`;
  };

  renderer.paragraph = function(text) {
    // 判断这个段落中是否只有图片，没有其他东西，如果是这样这个段落直接返回
    const imgReg = /^(\<p\>\<\/p\>)?(<img[^>]*>)+(\<p\>\<\/p\>)?$/g;
    const imgMatches = text.trim().match(imgReg);
    let result = `<p>${text}</p>`;
    if (imgMatches && imgMatches.length > 0) {
      result = `<div class='img-paragraph'> ${text} </div>`;
    }
    return result;
  };
  renderer.codespan = function(code) {
    // 处理双重转义的情况：&amp;lt; -> &lt;
    code = code.replace(/&amp;lt;/g, '&lt;');
    // 处理内联代码中的 u 标签
    code = code.replace(/&lt;u&gt;/g, '<u>').replace(/&lt;\/u&gt;/g, '</u>');
    // 将转义的尖括号还原为正常显示
    const result = `<code>${code}</code>`
    return result;
  };
  // 将带有类名、信息的 Markdown 转换为 HTML
  let result = str_reg_arr.map((str) => {
    if (/<div.*?<\/div>/.test(str) || notMarkdown) {
      return str;
    } else {
      return marked(str, {
        renderer: renderer,
      });
    }
  }).join("");
  result = restoreCustomTags(result, customEmojis);
  result = replaceBotKeywords(result)
  result = emojiRender.replaceEmojiByTextName(result, false, addition?.emoji_info);
  result = result.replace(/<iframe/g, "&lt;iframe");
  result = addCodeSpacing(result);
  return result;
}

export function regParser(str, msg) {
  // str = str.replace(/#点击链接直接加入房间#  \nhttp.*?\n#邀请码# (.{6})/g, (str, str_$1) => {
  //   return `<div class="invite-code-box"><div class="btn-accept-invite" data-invite-code="${str_$1}">接受邀请</div></div>`
  // })
  let user_id = store.getters.user_info.user_id, my_roles = store.getters.cur_my_roles
  let at_users = msg.at_users, at_roles = msg.at_roles, at_group_members = msg.at_group_members

  const escapeUsername = (name) => {
    return name.replace(/\\/g, "\\\\");
  };

  const renderAtStyle = (color, no_gradient = true) => {
    if (no_gradient) {
      return `color: ${color || "var(--opacity-6)"}`;
    }
    let style = "";
    if (Array.isArray(color) && color.length === 2) {
      style = `background-image: linear-gradient(90deg, ${color[0]} 0%, ${color[1]} 100%)`;
    } else {
      style = `color: ${color || "var(--opacity-6)"}`;
    }
    return style;
  };
  const newAtParser = (str, id, name) => {
    let class_str = "at";
    let isMe = id == user_id || my_roles.indexOf(`${id}`) >= 0
    if (isMe) {
      class_str += " me";
    }
    let index = -1;
    let at_users_info = null, at_roles_info = null, at_group_members_info = null;
    if (at_users) {
      index = getIndexFromArrayByKeyValue(at_users, "user_id", id);
      index >= 0 && (at_users_info = at_users[index]);
      if (at_users_info) {
        let color = at_users_info.color
          ? decimalToHexColor(at_users_info.color)
          : calcUserRoleColor(at_users_info.roles);
        const style = renderAtStyle(color, isMe);
        if (style.includes("background-image")) {
          class_str += " gradient";
        }
        const atName = getEmojiHtml(escapeUsername(calcUserName(at_users_info)), { width: "14px", height: "14px" }, "disable-preview")
        return `<at class="${class_str}" style="${style}" contenteditable="false" user_id="${at_users_info.user_id}">@${atName}</at>`;
      }
    }
    if (index == -1 && at_roles) {
      index = getIndexFromArrayByKeyValue(at_roles, "role_id", id);
      index >= 0 && (at_roles_info = at_roles[index]);
      if (at_roles_info) {
        let color = at_roles_info.color
          ? decimalToHexColor(at_roles_info.color)
          : calcUserRoleColor([at_roles_info.role_id]);
        const style = renderAtStyle(color, isMe);
        if (style.includes("background-image")) {
          class_str += " gradient";
        }
        const atName = getEmojiHtml(at_roles_info.name, { width: "14px", height: "14px" }, "disable-preview")
        return `<at class="${class_str}" style="${style}" contenteditable="false" roles_id="${at_roles_info.role_id}">@${atName}</at>`;
      }
    }
    if (index == -1 && at_group_members) {
      index = getIndexFromArrayByKeyValue(at_group_members, "group_member_id", id);
      index >= 0 && (at_group_members_info = at_group_members[index]);
      if (at_group_members_info) {
        let color = decimalToHexColor(at_group_members_info.color)
        const style = renderAtStyle(color, isMe);
        if (style.includes("background-image")) {
          class_str += " gradient";
        }
        const atName = getEmojiHtml(at_group_members_info.name, { width: "14px", height: "14px" }, "disable-preview")
        return `<at class="${class_str}" style="${style}" contenteditable="false" group_member_id="${at_group_members_info.group_member_id}">@${atName}</at>`;
      }
    }
    return str;
  };
  const channelParser = (str, id) => {
    let channel_info = null;
    if (msg.mention_channels) {
      let index = getIndexFromArrayByKeyValue(
        msg.mention_channels,
        "channel_id",
        id
      );
      index >= 0 && (channel_info = msg.mention_channels[index]);
    }
    if (channel_info) {
      return `<at class="channel-mentioned" contenteditable="false" channel_id="${channel_info.channel_id}">#${channel_info.channel_name}</at>`;
    } else {
      return `<span class="deleted-channel-mentioned">#频道已删除</span>`;
    }
  };
  let at_reg = /@{id:(\S+?)}/g;
  let channel_reg = /#{id:(\S+?)}/g;
  let str_at_reg = str.replaceAll(at_reg, newAtParser);
  str_at_reg = str_at_reg.replaceAll(channel_reg, channelParser);
  let div_regex = /(<\/?div[^>]*>)/;
  let arr = str_at_reg.split(div_regex);

  let result = [];
  let tempStr = "";
  let stackNum = 0;
  arr.forEach((s) => {
    let flag = div_regex.test(s);
    if (!flag && stackNum === 0) {
      result.push(s);
    } else {
      tempStr += s;
      if (flag && s.startsWith("</")) {
        stackNum--;
      } else if (flag) {
        stackNum++;
      }
      if (stackNum === 0) {
        result.push(tempStr);
        tempStr = "";
      }
    }
  });
  return result;
}

function checkIsOnRiskPic(href){
  if (!href) return 0
  const afterReplace = href.replace(pointReg, ".");
  const result = afterReplace.indexOf(ON_RISK_IMG_URL) !== -1
  return result ? 1 : 0
}